/**
 * The widget colour scheme: its two values, their labels, and the rule for reading a
 * stored one. (CHAT-995)
 *
 * The stored value is a string enum, not a boolean, so a third option later (system,
 * say) is an addition rather than a migration.
 *
 * The control is a segmented control whose options carry the stored values directly, so
 * nothing has to translate a boolean back into a string on save — the earlier switch did
 * need that, and the transient `darkTheme` key it required is gone with it. What is
 * shared here is the vocabulary: the two values, their labels and order, and the
 * normalisation rule. Both surfaces import them rather than keeping a copy.
 */
export type WidgetTheme = 'light' | 'dark';

export const WIDGET_THEME_LIGHT: WidgetTheme = 'light';
export const WIDGET_THEME_DARK: WidgetTheme = 'dark';

/**
 * The segmented control's options, so the two surfaces cannot drift apart on wording or
 * order. Both labels are always on screen — Light and Dark are peers, not a state and its
 * absence, which is why this is a segmented control and not a switch.
 */
export const WIDGET_THEME_OPTIONS: { value: WidgetTheme; label: string }[] = [
  { value: WIDGET_THEME_LIGHT, label: 'Light' },
  { value: WIDGET_THEME_DARK, label: 'Dark' },
];

/**
 * Is this stored value dark?
 *
 * Everything that is not exactly 'dark' is light — null, '', an unrecognised value.
 * That is what makes every bot and channel that exists today render unchanged: none of
 * them has a stored theme.
 */
export function isDarkTheme(theme: string | null | undefined): boolean {
  return (theme || '').trim().toLowerCase() === WIDGET_THEME_DARK;
}

/** Normalise a stored value to one of the two known ones. */
export function normalizeTheme(theme: string | null | undefined): WidgetTheme {
  return isDarkTheme(theme) ? WIDGET_THEME_DARK : WIDGET_THEME_LIGHT;
}
